import {cn} from '@sollapay/ui';

import {FormShell} from '@/components';
import {AiExtractionStatus} from '@/features/ai-extraction';
import {
  BUYERS_MODAL_FORM_CLASS,
  BUYERS_MODAL_GUTTER_CLASS,
  BUYERS_MODAL_GUTTER_MARGIN_CLASS,
  BUYERS_MODAL_SECTION_GROUP_CLASS
} from '@/features/buyers/constants';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {AssetDetailsSection} from './AssetDetailsSection';
import {BuyerDetailsSection} from './BuyerDetailsSection';
import {DocumentUploadSection} from './DocumentUploadSection';
import {PurchasePriceSection} from './PurchasePriceSection';
import {UnitAttachmentsSection} from './UnitAttachmentsSection';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {BuyerRowAi} from '@/features/buyers/hooks';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {FC} from 'react';
import type {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayReplace,
  UseFormReturn
} from 'react-hook-form';

const GUTTERED_GROUP_CLASS = cn(BUYERS_MODAL_SECTION_GROUP_CLASS, BUYERS_MODAL_GUTTER_CLASS);

export interface BuyerDetailsTabContentProps {
  trustAccountId: string;
  /** Unique per "Add Buyer" open — scopes document display/AI extraction to this session. */
  uploadSessionId: string;
  form: UseFormReturn<CreateBuyerFormValues>;
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[];
  appendBuyer: UseFieldArrayAppend<CreateBuyerFormValues, 'buyers'>;
  removeBuyer: (index: number) => void;
  replaceBuyers: UseFieldArrayReplace<CreateBuyerFormValues, 'buyers'>;
  onDocumentsChange: (documentIds: string[]) => void;
  saveError?: unknown;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  suggestionsCount: number;
  rowAi: BuyerRowAi;
  markFieldModified: (field: string) => void;
  onFillManually: () => void;
  reindexBuyerAiFields: (removedIndex: number) => void;
}

export const BuyerDetailsTabContent: FC<BuyerDetailsTabContentProps> = ({
  trustAccountId,
  uploadSessionId,
  form,
  buyerFields,
  appendBuyer,
  removeBuyer,
  replaceBuyers,
  onDocumentsChange,
  saveError,
  aiFields,
  isProcessing,
  suggestionsCount,
  rowAi,
  markFieldModified,
  onFillManually,
  reindexBuyerAiFields
}) => {
  const {collectsAssetPerPurchase} = useTrustTypeStrategy();
  const sectionProps = {form, aiFields, isProcessing, markFieldModified};

  return (
    <FormShell
      className={BUYERS_MODAL_FORM_CLASS}
      errorClassName={BUYERS_MODAL_GUTTER_MARGIN_CLASS}
      id="create-buyer-form"
      onSubmit={e => e.preventDefault()}
      saveError={saveError}
    >
      <div className={GUTTERED_GROUP_CLASS}>
        <DocumentUploadSection
          trustAccountId={trustAccountId}
          uploadSessionId={uploadSessionId}
          onDocumentsChange={onDocumentsChange}
        />
        <AiExtractionStatus
          isProcessing={isProcessing}
          suggestionsCount={suggestionsCount}
          onFillManually={onFillManually}
        />
      </div>
      <BuyerDetailsSection
        form={form}
        buyerFields={buyerFields}
        appendBuyer={appendBuyer}
        removeBuyer={removeBuyer}
        replaceBuyers={replaceBuyers}
        aiFields={aiFields}
        isProcessing={isProcessing}
        rowAi={rowAi}
        markFieldModified={markFieldModified}
        reindexBuyerAiFields={reindexBuyerAiFields}
      />
      <div className={GUTTERED_GROUP_CLASS}>
        {collectsAssetPerPurchase ? (
          <AssetDetailsSection {...sectionProps} />
        ) : (
          <PurchasePriceSection {...sectionProps} />
        )}
        {collectsAssetPerPurchase && <UnitAttachmentsSection {...sectionProps} />}
      </div>
    </FormShell>
  );
};
